/**
 * Product card cert flip — flip state for collection grid cards.
 * Front: zone-native PDP links, CTA passthrough, active cert flip.
 * Back: click anywhere closes. Face inert is a11y; hit-test uses CSS PE none.
 */
(function () {
  'use strict';

  if (window.__BK_CARD_FLIP_INIT) return;
  window.__BK_CARD_FLIP_INIT = true;

  const BADGE_SELECTOR = '.cert-badge:not(.cert-badge--inactive):not(.cert-badge--locked):not([data-cert-intel-locked])';
  const CARD_BAR_SELECTOR = '.cert-badges-bar--card';
  const FILTER_BAR_SELECTOR = '.cert-badges-bar--filter';
  const WRAPPER_SELECTOR = '.product-card-wrapper';
  const FLIP_SCENE_SELECTOR = '.product-card-wrapper.product-card__flip-scene';
  const FLIPPED_CLASS = 'is-cert-flipped';
  const CERT_CLASSES = ['cert-badge--peta', 'cert-badge--made-safe', 'cert-badge--ewg', 'cert-badge--skinsafe'];
  const CERT_KEYS = ['peta', 'made-safe', 'ewg', 'skinsafe'];

  function isLockedBadge(badge) {
    if (!(badge instanceof Element)) return true;
    if (badge.hasAttribute('data-cert-intel-locked')) return true;
    if (badge.classList.contains('cert-badge--locked')) return true;
    return false;
  }

  /** @type {WeakMap<Element, ResizeObserver>} */
  const heroObservers = new WeakMap();

  function certKeyFromBadge(badge) {
    for (let i = 0; i < CERT_CLASSES.length; i += 1) {
      if (badge.classList.contains(CERT_CLASSES[i])) return CERT_KEYS[i];
    }
    return null;
  }

  function wrapperForBadge(badge) {
    return badge.closest(WRAPPER_SELECTOR);
  }

  function faceEls(wrapper) {
    return {
      front: wrapper.querySelector('.product-card__face--front'),
      back: wrapper.querySelector('.product-card__face--back'),
    };
  }

  function panels(wrapper) {
    return wrapper.querySelectorAll('[data-cert-panel]');
  }

  function activePanel(wrapper, certKey) {
    return wrapper.querySelector(`[data-cert-panel="${certKey}"]`);
  }

  function offsetWithinPanel(el, panel) {
    let x = 0;
    let y = 0;
    let node = el;
    while (node && node !== panel) {
      x += node.offsetLeft;
      y += node.offsetTop;
      node = node.parentElement;
    }
    return { x, y };
  }

  const FOIL_HERO_MIN_GAP = 8;

  function layoutFoilHero(panel) {
    if (!(panel instanceof HTMLElement)) return;

    const icon = panel.querySelector('.cert-card-back__icon');
    const titlePlate = panel.querySelector('.cert-card-back__title-plate');
    if (!icon || !titlePlate) return;

    const iconWrap = panel.querySelector('.cert-card-back__icon-wrap') ?? icon;
    const anchorBottom = offsetWithinPanel(iconWrap, panel).y + iconWrap.offsetHeight;
    const titleTop = offsetWithinPanel(titlePlate, panel).y;
    const heroCenterY = (anchorBottom + titleTop) / 2;
    const gap = titleTop - anchorBottom;

    panel.style.setProperty('--cert-foil-hero-y', `${heroCenterY}px`);
    panel.style.removeProperty('--cert-foil-hero-clip-top');
    panel.style.removeProperty('--cert-foil-hero-clip-height');
    /* Full-panel shine (v178); do not set --cert-foil-shine-clip-top. */
    panel.style.removeProperty('--cert-foil-shine-clip-top');

    panel.classList.toggle('is-foil-hero-collapsed', gap < FOIL_HERO_MIN_GAP);
  }

  function scheduleFoilHeroLayout(panel) {
    if (!(panel instanceof HTMLElement)) return;
    requestAnimationFrame(() => {
      layoutFoilHero(panel);
      requestAnimationFrame(() => layoutFoilHero(panel));
    });
  }

  function disconnectHeroObserver(panel) {
    const observer = heroObservers.get(panel);
    if (!observer) return;
    observer.disconnect();
    heroObservers.delete(panel);
  }

  function connectHeroObserver(panel) {
    if (!(panel instanceof HTMLElement) || typeof ResizeObserver === 'undefined') return;

    disconnectHeroObserver(panel);

    const observer = new ResizeObserver(() => layoutFoilHero(panel));
    observer.observe(panel);
    heroObservers.set(panel, observer);
  }

  function syncFoilHeroForWrapper(wrapper) {
    if (!wrapper) return;

    panels(wrapper).forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      if (panel.classList.contains('is-active')) {
        connectHeroObserver(panel);
        scheduleFoilHeroLayout(panel);
      } else {
        disconnectHeroObserver(panel);
      }
    });
  }

  function setFaceVisibility(wrapper, flipped) {
    const { front, back } = faceEls(wrapper);
    if (front) {
      front.setAttribute('aria-hidden', flipped ? 'true' : 'false');
      front.inert = Boolean(flipped);
    }
    if (back) {
      back.setAttribute('aria-hidden', flipped ? 'false' : 'true');
      back.inert = !flipped;
    }
  }

  function dismissBadgeTips(wrapper) {
    wrapper.querySelectorAll(BADGE_SELECTOR).forEach((badge) => {
      badge.classList.remove('is-open');
      badge.setAttribute('aria-expanded', 'false');
      const tip = badge.querySelector('.cert-badge__tip');
      if (tip) tip.hidden = true;
    });
  }

  function activatePanel(wrapper, certKey) {
    panels(wrapper).forEach((panel) => {
      panel.classList.toggle('is-active', panel.dataset.certPanel === certKey);
    });
    syncFoilHeroForWrapper(wrapper);
    refitTitle(wrapper);
  }

  function runFlipToggle(wrapper, beginFlip, onSettled) {
    if (window.productCardTilt?.onFlipStart) {
      window.productCardTilt.onFlipStart(wrapper, beginFlip, onSettled);
      return;
    }
    if (typeof beginFlip === 'function') beginFlip();
    if (typeof onSettled === 'function') onSettled();
  }

  function closeFlip(wrapper) {
    if (!wrapper || !wrapper.classList.contains(FLIPPED_CLASS)) return;
    if (wrapper.classList.contains('is-flip-animating')) return;

    dismissBadgeTips(wrapper);

    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && wrapper.contains(activeElement)) {
      activeElement.blur();
    }

    /* beginFlip toggles flip class inside onFlipStart (parallel with tilt envelope).
       Keep .is-active + data-cert-active until settle — reverse surface is transparent;
       dropping the opaque panel at spin start floods midplane gilt (gilt-close-panel-hold-v1). */
    runFlipToggle(
      wrapper,
      () => {
        wrapper.classList.remove(FLIPPED_CLASS);
        setFaceVisibility(wrapper, false);
      },
      () => {
        panels(wrapper).forEach((panel) => {
          panel.classList.remove('is-active');
          disconnectHeroObserver(panel);
        });
        wrapper.removeAttribute('data-cert-active');
      }
    );
  }

  function refitTitle(wrapper) {
    if (!wrapper) return;
    window.trustCertTitleFit?.refit?.(wrapper);
  }

  function openFlip(wrapper, certKey) {
    const panel = activePanel(wrapper, certKey);
    if (!panel) return;
    if (wrapper.classList.contains('is-flip-animating')) return;

    dismissBadgeTips(wrapper);
    activatePanel(wrapper, certKey);
    wrapper.setAttribute('data-cert-active', certKey);

    runFlipToggle(
      wrapper,
      () => {
        wrapper.classList.add(FLIPPED_CLASS);
        setFaceVisibility(wrapper, true);
      },
      () => {
        syncFoilHeroForWrapper(wrapper);
        layoutFoilHero(panel);
        refitTitle(wrapper);
      }
    );
    scheduleFoilHeroLayout(panel);
    requestAnimationFrame(() => {
      refitTitle(wrapper);
      requestAnimationFrame(() => refitTitle(wrapper));
    });
  }

  function flipToCert(badge) {
    if (isLockedBadge(badge)) return false;

    const bar = badge.closest(CARD_BAR_SELECTOR);
    if (!bar) return false;

    const wrapper = wrapperForBadge(badge);
    if (!wrapper) return false;

    const certKey = certKeyFromBadge(badge);
    if (!certKey) return false;

    const alreadyFlipped = wrapper.classList.contains(FLIPPED_CLASS);
    const sameCert = wrapper.getAttribute('data-cert-active') === certKey;

    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach((other) => {
      if (other !== wrapper) closeFlip(other);
    });

    if (alreadyFlipped && sameCert) {
      closeFlip(wrapper);
      return true;
    }

    openFlip(wrapper, certKey);
    return true;
  }

  function onBadgeFlipClick(event) {
    const badge = event.target.closest(`${CARD_BAR_SELECTOR} ${BADGE_SELECTOR}`);
    if (!badge) return;

    const wrapper = badge.closest(FLIP_SCENE_SELECTOR);
    if (!wrapper || wrapper.classList.contains('is-flip-animating')) return;
    if (wrapper.classList.contains(FLIPPED_CLASS)) return;

    if (flipToCert(badge)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }

  function onCardClick(event) {
    const wrapper = event.target.closest(FLIP_SCENE_SELECTOR);
    if (!wrapper || wrapper.classList.contains('is-flip-animating')) return;

    if (wrapper.classList.contains(FLIPPED_CLASS)) {
      event.preventDefault();
      closeFlip(wrapper);
      return;
    }

    if (event.target.closest(`${CARD_BAR_SELECTOR} ${BADGE_SELECTOR}`)) {
      const badge = event.target.closest('.cert-badge');
      if (badge && flipToCert(badge)) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  function onOutsideClick(event) {
    if (event.target.closest(WRAPPER_SELECTOR)) return;
    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
  }

  document.addEventListener('click', onBadgeFlipClick, true);
  document.addEventListener('click', onCardClick);
  document.addEventListener('click', onOutsideClick);

  /* CMS: reverse stays open on leave — close via reverse click / Escape / outside only. */

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(syncFoilHeroForWrapper);
    document.querySelectorAll('.cert-foil-live .cert-card-back__panel.is-active').forEach((panel) => {
      if (panel instanceof HTMLElement) {
        connectHeroObserver(panel);
        scheduleFoilHeroLayout(panel);
      }
    });
  });

  function initLiveFoilHero() {
    document.querySelectorAll('.cert-foil-live .cert-card-back__panel.is-active').forEach((panel) => {
      if (!(panel instanceof HTMLElement)) return;
      connectHeroObserver(panel);
      scheduleFoilHeroLayout(panel);
    });
  }

  function bootCardFlip() {
    initLiveFoilHero();
  }

  /* Wait for DOMContentLoaded so sibling defer scripts (tilt) register APIs first.
     Do not boot synchronously at interactive — that races before product-card-tilt.js. */
  if (document.readyState === 'complete') {
    bootCardFlip();
  } else {
    document.addEventListener('DOMContentLoaded', bootCardFlip);
  }

  window.productCardCertFlip = {
    flipToCert,
    close: closeFlip,
    closeAll() {
      document.querySelectorAll(`${WRAPPER_SELECTOR}.${FLIPPED_CLASS}`).forEach(closeFlip);
    },
    isCardGridBadge(badge) {
      return Boolean(badge && badge.closest(CARD_BAR_SELECTOR));
    },
    layoutFoilHero,
  };
})();
